<template>
  <div
    ref="inboxRef"
    class="notification-inbox"
    data-test="notification-inbox"
  >
    <button
      class="inbox-bell"
      data-test="inbox-bell"
      :aria-label="$t('inbox.title')"
      @click="togglePanel"
    >
      <i class="fas fa-bell" />
      <span
        v-if="store.hasUnread"
        class="unread-badge"
        data-test="inbox-unread-badge"
      >
        {{ store.unreadCount > 9 ? '9+' : store.unreadCount }}
      </span>
    </button>

    <transition name="inbox-fade">
      <div
        v-if="isOpen"
        class="inbox-panel"
        data-test="inbox-panel"
      >
        <div class="inbox-header">
          <h4>{{ $t('inbox.title') }}</h4>
          <button
            v-if="store.hasUnread"
            class="mark-all-read"
            data-test="inbox-mark-all"
            @click="store.markAllRead()"
          >
            {{ $t('inbox.mark-all-read') }}
          </button>
        </div>

        <div
          v-if="store.isLoading"
          class="inbox-loading"
        >
          <i class="fas fa-circle-notch fa-spin" />
        </div>

        <div
          v-else-if="!store.items.length"
          class="inbox-empty"
          data-test="inbox-empty"
        >
          <i class="fas fa-bell-slash" />
          <p>{{ $t('inbox.empty') }}</p>
          <p class="empty-hint">
            {{ $t('inbox.empty-hint') }}
          </p>
        </div>

        <ul
          v-else
          class="inbox-list"
        >
          <li
            v-for="notification in store.items"
            :key="notification._id"
            class="inbox-item"
            :class="{ 'is-unread': !notification.read }"
            data-test="inbox-item"
            @click="openNotification(notification)"
          >
            <div
              class="item-icon"
              :class="`is-${notification.type || 'info'}`"
            >
              <i :class="iconFor(notification.type)" />
            </div>
            <div class="item-body">
              <p class="item-title">
                {{ notification.title }}
              </p>
              <p class="item-text">
                {{ notification.body }}
              </p>
              <p class="item-date">
                {{ formatDate(notification.createdAt) }}
              </p>
            </div>
            <span
              v-if="!notification.read"
              class="unread-dot"
            />
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useNotificationsStore } from '@/store/notifications';

const router = useRouter();
const { locale } = useI18n();
const store = useNotificationsStore();

const isOpen = ref(false);
const inboxRef = ref(null);

const ICONS = {
  milestone: 'fas fa-trophy',
  release: 'fas fa-compact-disc',
  royalty: 'fas fa-file-invoice-dollar',
  audience: 'fas fa-heart',
  recap: 'fas fa-chart-line',
  info: 'fas fa-info-circle',
};

const iconFor = (type) => ICONS[type] || ICONS.info;

const formatDate = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat(locale.value, {
    dateStyle: 'medium',
  }).format(new Date(value));
};

const togglePanel = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    store.fetchNotifications({ limit: 20 });
  }
};

const openNotification = (notification) => {
  store.markRead(notification._id);
  if (notification.link) {
    isOpen.value = false;
    router.push(notification.link);
  }
};

const handleOutsideClick = (event) => {
  if (inboxRef.value && !inboxRef.value.contains(event.target)) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
  // Load the unread count once so the badge is accurate on page load
  store.fetchNotifications({ limit: 20 });
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
});
</script>

<style lang="scss" scoped>
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/breakpoints";

.notification-inbox {
  position: relative;
  display: flex;
  align-items: center;

  .inbox-bell {
    position: relative;
    background: transparent;
    border: none;
    cursor: pointer;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-light);
    font-size: 1.1rem;
    transition: all 0.2s ease;

    &:hover {
      background: rgba($indiefy-blue, 0.1);
      color: var(--color-primary, $indiefy-blue);
    }

    .unread-badge {
      position: absolute;
      top: 0;
      right: 0;
      min-width: 17px;
      height: 17px;
      padding: 0 4px;
      border-radius: 999px;
      background: $indiefy-pink;
      color: white;
      font-size: 0.65rem;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 6px rgba($indiefy-pink, 0.5);
    }
  }

  .inbox-panel {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    width: 360px;
    max-width: 92vw;
    max-height: 440px;
    overflow-y: auto;
    background: var(--color-background, #fff);
    border-radius: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
    border: 1px solid rgba(0, 0, 0, 0.06);
    z-index: 60;

    .body--dark & {
      background: #1a202c;
      border-color: rgba(255, 255, 255, 0.08);
    }

    @media screen and (max-width: $mobile) {
      position: fixed;
      top: 60px;
      left: 50%;
      right: auto;
      transform: translateX(-50%);
      width: 94vw;
    }

    .inbox-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem 1.25rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
      position: sticky;
      top: 0;
      background: inherit;
      z-index: 1;

      .body--dark & {
        border-color: rgba(255, 255, 255, 0.08);
      }

      h4 {
        font-weight: 700;
        font-size: 1rem;
        color: var(--color-text);

        .body--dark & {
          color: #fff;
        }
      }

      .mark-all-read {
        background: transparent;
        border: none;
        cursor: pointer;
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--color-primary, $indiefy-blue);

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .inbox-loading {
      display: flex;
      justify-content: center;
      padding: 2rem;
      color: var(--color-primary, $indiefy-blue);
      font-size: 1.3rem;
    }

    .inbox-empty {
      text-align: center;
      padding: 2.5rem 1.5rem;

      i {
        font-size: 1.8rem;
        color: var(--color-text-light);
        margin-bottom: 0.75rem;
      }

      p {
        font-weight: 600;
        font-size: 0.92rem;
        color: var(--color-text);

        .body--dark & {
          color: #e2e8f0;
        }
      }

      .empty-hint {
        font-weight: 400;
        font-size: 0.82rem;
        color: var(--color-text-light);
        margin-top: 0.3rem;
      }
    }

    .inbox-list {
      .inbox-item {
        display: flex;
        align-items: flex-start;
        gap: 0.85rem;
        padding: 0.9rem 1.25rem;
        cursor: pointer;
        transition: background-color 0.15s ease;
        position: relative;

        &:hover {
          background: rgba($indiefy-blue, 0.05);
        }

        &.is-unread {
          background: rgba($indiefy-blue, 0.04);

          .body--dark & {
            background: rgba($indiefy-blue, 0.1);
          }
        }

        .item-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 0.85rem;
          flex-shrink: 0;

          &.is-milestone {
            background: linear-gradient(135deg, #f6d365, #fda085);
          }

          &.is-release {
            background: linear-gradient(135deg, $indiefy-blue, $indiefy-teal);
          }

          &.is-royalty {
            background: linear-gradient(135deg, $indiefy-teal, darken($indiefy-teal, 12%));
          }

          &.is-audience {
            background: linear-gradient(135deg, $indiefy-pink, $indiefy-lavender);
          }

          &.is-recap,
          &.is-info {
            background: linear-gradient(135deg, $indiefy-lavender, $indiefy-blue);
          }
        }

        .item-body {
          flex: 1;
          min-width: 0;

          .item-title {
            font-weight: 700;
            font-size: 0.88rem;
            color: var(--color-text);

            .body--dark & {
              color: #fff;
            }
          }

          .item-text {
            font-size: 0.82rem;
            color: var(--color-text-light);
            margin-top: 0.15rem;
            line-height: 1.4;
          }

          .item-date {
            font-size: 0.72rem;
            color: var(--color-text-light);
            opacity: 0.7;
            margin-top: 0.3rem;
          }
        }

        .unread-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: $indiefy-pink;
          flex-shrink: 0;
          margin-top: 0.4rem;
        }
      }
    }
  }
}

.inbox-fade-enter-active,
.inbox-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.inbox-fade-enter-from,
.inbox-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
